import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { Inbox, ExternalLink, Eye, Search } from 'lucide-react';

// Spec-driven scenario labels. Lookup table kept here so we can change
// wording in one place if Product asks.
const SCENARIO_LABELS = {
  1: 'Replied then ghosted',
  2: 'Submitted, no response',
  3: "Saw post, didn't submit"
};

const STATUS_BADGE = {
  pending: 'badge-amber',
  sent: 'badge-blue',
  converted: 'badge-green',
  closed: 'badge-gray'
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const UpworkReferrals = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [referrals, setReferrals] = useState([]);
  const [counts, setCounts] = useState({
    pending: 0,
    sent: 0,
    converted_last_30_days: 0,
    closed_last_30_days: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchReferrals = async (status) => {
    setLoading(true);
    try {
      const res = await api.get('/upwork-referrals', { params: { status } });
      setReferrals(res.data.referrals || []);
    } catch (err) {
      console.error('Fetch upwork referrals error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const res = await api.get('/upwork-referrals/counts');
      setCounts(res.data);
    } catch (err) {
      console.error('Fetch upwork counts error:', err);
    }
  };

  // Row dropdown: pending -> sent. Optimistically drop the row from the
  // Pending tab (it's no longer pending), then confirm with the server.
  // On failure, refetch to restore. Refresh counts + sidebar badge on success.
  const markSent = async (id) => {
    const prev = referrals;
    setReferrals((rs) => rs.filter((r) => r.id !== id));
    try {
      await api.post(`/upwork-referrals/${id}/sent`);
      toast.success('Marked as sent.');
      fetchCounts();
      window.dispatchEvent(new Event('upwork-referrals-changed'));
    } catch (err) {
      setReferrals(prev);
      toast.error(err.response?.data?.error || 'Failed to mark as sent.');
    }
  };

  useEffect(() => { fetchCounts(); }, []);
  useEffect(() => { fetchReferrals(activeTab); }, [activeTab]);

  // Client-side search filter — server returns everything for the chosen
  // status; this narrows the rendered list.
  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? referrals.filter(r =>
        (r.upwork_job_title || '').toLowerCase().includes(q) ||
        (r.original_upwork_bde_name || '').toLowerCase().includes(q) ||
        (r.linkedin_url || '').toLowerCase().includes(q)
      )
    : referrals;

  return (
    <div>
      <div className="page-header">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Inbox size={24} color="var(--blue)" /> Upwork Referrals
        </h2>
        <p>Prospects forwarded from the Upwork BDE team for LinkedIn outreach</p>
      </div>

      {/* KPI strip */}
      <div className="card" style={{
        marginBottom: 16,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 16
      }}>
        <div>
          <div style={{
            fontSize: 12, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em'
          }}>Pending</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#1F3A5F', marginTop: 4 }}>
            {counts.pending}
          </div>
        </div>
        <div>
          <div style={{
            fontSize: 12, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em'
          }}>Sent</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#1D4ED8', marginTop: 4 }}>
            {counts.sent}
          </div>
        </div>
        <div>
          <div style={{
            fontSize: 12, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em'
          }}>Converted (last 30 days)</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#166534', marginTop: 4 }}>
            {counts.converted_last_30_days}
          </div>
        </div>
        <div>
          <div style={{
            fontSize: 12, color: '#6B7280',
            textTransform: 'uppercase', letterSpacing: '0.05em'
          }}>Closed (last 30 days)</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#6B7280', marginTop: 4 }}>
            {counts.closed_last_30_days}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--gray-200)', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 24 }}>
          {[
            { key: 'pending',   label: 'Pending Review', badge: counts.pending },
            { key: 'sent',      label: 'Sent', badge: counts.sent },
            { key: 'converted', label: 'Converted' },
            { key: 'closed',    label: 'Closed' }
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '12px 4px', fontWeight: 600, fontSize: 14,
                color: activeTab === t.key ? 'var(--blue)' : 'var(--gray-500)',
                borderBottom: activeTab === t.key ? '2px solid var(--blue)' : '2px solid transparent',
                marginBottom: -1
              }}
            >
              {t.label}
              {t.badge > 0 && (
                <span className="badge badge-blue" style={{ marginLeft: 6 }}>{t.badge}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{
            position: 'absolute', left: 12, top: '50%',
            transform: 'translateY(-50%)', color: 'var(--gray-500)',
            pointerEvents: 'none'
          }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by job title, Upwork BDE name, or LinkedIn URL..."
            style={{
              width: '100%', padding: '8px 12px 8px 36px',
              border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 14
            }}
          />
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading referrals...</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <Inbox size={32} />
          <p>{searchQuery ? 'No referrals match your search.' : `No ${activeTab} referrals.`}</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>From (Upwork BDE)</th>
                  <th>LinkedIn</th>
                  <th>Job Title</th>
                  <th>Scenario</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr
                    key={r.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/upwork-referrals/${r.id}`)}
                  >
                    <td style={{
                      fontSize: 13, color: 'var(--gray-700)', whiteSpace: 'nowrap'
                    }}>{formatDate(r.created_at)}</td>
                    <td>{r.original_upwork_bde_name}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <a
                        href={r.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: 'var(--blue)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        Profile <ExternalLink size={12} />
                      </a>
                    </td>
                    <td
                      style={{
                        maxWidth: 320, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}
                      title={r.upwork_job_title}
                    >
                      {r.upwork_job_title}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--gray-600)' }}>
                      {SCENARIO_LABELS[r.scenario] || r.scenario}
                    </td>
                    {/* Pending rows get a small Pending/Sent dropdown so Gurkirat
                        can mark a sent connection request without opening the row.
                        All other rows/tabs keep the plain status badge. */}
                    {activeTab === 'pending' && r.status === 'pending' ? (
                      <td onClick={(e) => e.stopPropagation()}>
                        <select
                          value={r.status}
                          onChange={(e) => { if (e.target.value === 'sent') markSent(r.id); }}
                          style={{
                            fontSize: 12, fontWeight: 600, padding: '3px 8px',
                            borderRadius: 12, border: '1px solid var(--gray-300)',
                            background: '#FFFFFF', color: 'var(--gray-700)', cursor: 'pointer'
                          }}
                        >
                          <option value="pending">Pending</option>
                          <option value="sent">Sent</option>
                        </select>
                      </td>
                    ) : (
                      <td>
                        <span className={`badge ${STATUS_BADGE[r.status]}`}>{r.status}</span>
                      </td>
                    )}
                    <td>
                      <Eye size={16} color="var(--gray-500)" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpworkReferrals;
